import { useReducer, useEffect, useCallback } from 'react';
import { loadSongs, getRandomSong } from '../utils/gameData';
import { gameReducer, initialGameState, gameActionTypes } from './gameReducer';

const useGame = () => {
  const [state, dispatch] = useReducer(gameReducer, initialGameState);
  // Helper to show messages (keeps same API)
  const showMessage = useCallback((text, type = 'info', playerId = null) => {
    const id = Date.now() + Math.random();
    const newMessage = { id, text, type, playerId };
    dispatch({ type: gameActionTypes.SHOW_MESSAGE, payload: { message: newMessage } });
    setTimeout(() => {
      dispatch({
        type: gameActionTypes.REMOVE_EXPIRED_MESSAGES,
        payload: {},
      });
    }, 8000); // Increased from 5000ms to 8000ms
  }, []);

  // Remove expired messages
  useEffect(() => {
    if (state.messages.some((msg) => msg.remove)) {
      dispatch({
        type: 'REMOVE_EXPIRED_MESSAGES',
        payload: {},
      });
    }
  }, [state.messages]);

  const nextRound = useCallback(
    (gameData = state.songs) => {
      // Only check if there are any songs left in the deck
      if (!gameData || gameData.length === 0) {
        dispatch({ type: gameActionTypes.GAME_FINISHED });
        showMessage('🎉 Game over! All songs placed!', 'success');
        return;
      }
      const newSong = getRandomSong(gameData, []); // No usedIds needed, deck is always current
      if (!newSong) {
        dispatch({ type: gameActionTypes.GAME_FINISHED });
        showMessage('🎉 Game over! All songs played!', 'success');
        return;
      }
      dispatch({ type: gameActionTypes.SET_CURRENT_SONG, payload: { currentSong: newSong } });
      dispatch({ type: gameActionTypes.REVEAL_SONG, payload: { reveal: false } });
      dispatch({ type: gameActionTypes.REVEAL_YEAR, payload: { reveal: false } });
      dispatch({ type: gameActionTypes.NEXT_ROUND, payload: { currentSong: newSong } });
    },
    [state.songs, showMessage]
  );

  const startNewGame = useCallback(() => {
    dispatch({ type: gameActionTypes.START_NEW_GAME });
  }, []);

  

  const handleSelectCollection = useCallback(
    async (filePath) => {
      dispatch({ type: gameActionTypes.SELECT_COLLECTION, payload: { filePath } });
      const gameData = await loadSongs(filePath);
      // Seed each player with a unique song and track all used IDs
      const usedIds = [];
      const seedSongs = [];
      const newPlayers = state.players.map((player) => {
        const seedSong = getRandomSong(gameData.songs, usedIds);
        if (seedSong) {
          usedIds.push(seedSong.id);
          seedSongs.push(seedSong.id);
        }
        return {
          ...player,
          timeline: seedSong
            ? [{ ...seedSong, isSeed: true, revealed: true }]
            : [],
          score: 0,
          streak: 0,
        };
      });
      // Remove seed songs from the deck before starting the game
      const filteredSongs = gameData.songs.filter((s) => !seedSongs.includes(s.id));
      dispatch({
        type: gameActionTypes.SONGS_LOADED,
        payload: { songs: filteredSongs, players: newPlayers },
      });
      nextRound(filteredSongs, []); // Only use remaining songs
      showMessage('🎮 New game started! Player 1 goes first!', 'info');
    },
    [state.players, nextRound, showMessage]
  );

  const revealSong = useCallback(() => {
    dispatch({ type: gameActionTypes.REVEAL_SONG, payload: { reveal: true } });
  }, []);

  const revealYear = useCallback(() => {
    dispatch({ type: gameActionTypes.REVEAL_YEAR, payload: { reveal: true } });
  }, []);

  // --- Refactored: Remove played/discarded songs from the songs array and push to discardedSongs ---
  // Helper: Remove a song from the songs array by id and add to discardedSongs
  function removeAndDiscardSong(songs, discardedSongs, songId) {
    const idx = songs.findIndex((s) => s.id === songId);
    if (idx === -1) return { songs, discardedSongs };
    const [removed] = songs.splice(idx, 1);
    return {
      songs: [...songs],
      discardedSongs: removed ? [...discardedSongs, removed] : [...discardedSongs],
    };
  }

  const handleCorrectPlacement = useCallback(
    (position, playerId) => {
      const originalId = state.currentSong.id.split('_placed_')[0];
      const newSong = {
        ...state.currentSong,
        isNew: true,
        id: `${originalId}_placed_${Date.now()}`,
      };
      // Remove from songs and add to discardedSongs
      const { songs: newSongs, discardedSongs: newDiscards } = removeAndDiscardSong(
        [...state.songs],
        [...(state.discardedSongs || [])],
        originalId
      );
      dispatch({
        type: gameActionTypes.PLACE_SONG,
        payload: {
          position,
          playerId,
          song: newSong,
          players: state.players.map((player) => {
            if (player.id === playerId) {
              const newTimeline = [...player.timeline];
              newTimeline.splice(position, 0, newSong);
              return {
                ...player,
                timeline: newTimeline.sort((a, b) => a.release_year - b.release_year),
                score: player.score + 10 + player.streak,
                streak: player.streak + 1,
              };
            }
            return player;
          }),
          songs: newSongs,
          discardedSongs: newDiscards,
        },
      });
      dispatch({ type: gameActionTypes.SET_CURRENT_SONG, payload: { currentSong: null } });
      const playerName = state.players.find((p) => p.id === playerId)?.name || `Player ${playerId}`;
      showMessage(
        `🎉 Correct! ${playerName} placed ${state.currentSong.title} (${state.currentSong.release_year}) correctly!`,
        'success',
        playerId
      );
      setTimeout(() => {
        nextRound(newSongs, []); // Only use remaining songs
      }, 1500);
    },
    [state.currentSong, state.players, showMessage, nextRound, state.songs, state.discardedSongs]
  );

  const handleIncorrectPlacement = useCallback(
    (position, correctPosition, playerId) => {
      const originalId = state.currentSong.id.split('_placed_')[0];
      const activePlayer = state.players.find((p) => p.id === playerId);
      // Remove from songs and add to discardedSongs
      const { songs: newSongs, discardedSongs: newDiscards } = removeAndDiscardSong(
        [...state.songs],
        [...(state.discardedSongs || [])],
        originalId
      );
      dispatch({
        type: gameActionTypes.PLACE_SONG,
        payload: {
          position,
          playerId,
          song: { ...state.currentSong },
          players: state.players.map((player) => {
            if (player.id === playerId) {
              return { ...player, streak: 0 };
            }
            return player;
          }),
          songs: newSongs,
          discardedSongs: newDiscards,
        },
      });
      const year = state.currentSong.release_year;
      const timeline = activePlayer?.timeline || [];
      let feedbackMessage = `❌ Wrong! "${state.currentSong.title}" is from ${year}. `;
      if (correctPosition === 0) {
        feedbackMessage += `It should be placed at the beginning (before ${timeline[0]?.title} from ${timeline[0]?.release_year}).`;
      } else if (correctPosition === timeline.length) {
        feedbackMessage += `It should be placed at the end (after ${timeline[timeline.length - 1]?.title} from ${timeline[timeline.length - 1]?.release_year}).`;
      } else {
        const beforeSong = timeline[correctPosition];
        const afterSong = timeline[correctPosition - 1];
        feedbackMessage += `It should be placed between ${afterSong?.title} (${afterSong?.release_year}) and ${beforeSong?.title} (${beforeSong?.release_year}).`;
      }
      showMessage(feedbackMessage, 'error', playerId);
      const nextPlayer = state.currentPlayer === 1 ? 2 : 1;
      const nextPlayerName = state.players.find((p) => p.id === nextPlayer)?.name || `Player ${nextPlayer}`;
      setTimeout(() => {
        dispatch({ type: gameActionTypes.SET_CURRENT_PLAYER, payload: { playerId: nextPlayer } });
        dispatch({ type: gameActionTypes.SET_GAME_STATE, payload: { gameState: 'playing' } });
        dispatch({ type: gameActionTypes.SET_CURRENT_SONG, payload: { currentSong: null } });
        showMessage(`🔄 ${nextPlayerName}'s turn!`, 'info', nextPlayer);
        nextRound(newSongs, []); // Only use remaining songs
      }, 3000);
    },
    [state.currentSong, state.players, showMessage, state.currentPlayer, nextRound, state.songs, state.discardedSongs]
  );

  const isValidPlacement = (song, timeline, position) => {
    // Insert the song at the given position and check if the timeline is sorted by year (non-decreasing)
    const newTimeline = [...timeline];
    newTimeline.splice(position, 0, song);
    for (let i = 1; i < newTimeline.length; i++) {
      if (newTimeline[i - 1].release_year > newTimeline[i].release_year) {
        return false;
      }
    }
    return true;
  };

  const handleSongPlacement = useCallback(
    (position, playerId) => {
      if (!state.currentSong) return;
      if (typeof position !== 'number' || isNaN(position) || position < 0) return;
      if (typeof playerId !== 'number' || isNaN(playerId) || playerId < 1) return;
      if (playerId !== state.currentPlayer) return;
      const activePlayer = state.players.find((p) => p.id === state.currentPlayer);
      if (!activePlayer) return;
      if (position > activePlayer.timeline.length) return;
      if (isValidPlacement(state.currentSong, activePlayer.timeline, position)) {
        handleCorrectPlacement(position, playerId);
      } else {
        // Find the closest valid position for feedback
        let correctPosition = 0;
        for (let i = 0; i <= activePlayer.timeline.length; i++) {
          if (isValidPlacement(state.currentSong, activePlayer.timeline, i)) {
            correctPosition = i;
            break;
          }
        }
        handleIncorrectPlacement(position, correctPosition, playerId);
      }
    },
    [state.currentSong, state.currentPlayer, state.players, handleCorrectPlacement, handleIncorrectPlacement]
  );

  const handlePlayerNameChange = useCallback((playerId, newName) => {
    dispatch({ type: gameActionTypes.SET_PLAYER_NAME, payload: { playerId, newName } });
  }, []);

  // --- Song state debug logging ---
  // Log song state transitions for debugging deck/discard logic
  useEffect(() => {
    // Log on every song state change
    console.log('[SONG STATE] Remaining songs:', state.songs.map(s => s.id));
    console.log('[SONG STATE] Discarded songs:', (state.discardedSongs || []).map(s => s.id));
    if (state.currentSong) {
      console.log('[SONG STATE] Current song:', state.currentSong.id, state.currentSong.title);
    }
  }, [state.songs, state.discardedSongs, state.currentSong]);

  return {
    songs: state.songs,
    currentSong: state.currentSong,
    round: state.round,
    gameState: state.gameState,
    messages: state.messages.filter((msg) => !msg.remove),
    songRevealed: state.songRevealed,
    yearRevealed: state.yearRevealed,
    players: state.players,
    currentPlayer: state.currentPlayer,
    selectedCollectionFile: state.selectedCollectionFile,
    handleSelectCollection,
    startNewGame,
    revealSong,
    revealYear,
    handleSongPlacement,
    showMessage,
    handlePlayerNameChange,
    setGameState: (payload) => dispatch({ type: gameActionTypes.SET_GAME_STATE, payload }),
    setCurrentSong: (payload) => dispatch({ type: gameActionTypes.SET_CURRENT_SONG, payload }),
  };
};

export default useGame;
